import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, ActionIcon, Divider, Skeleton } from '@mantine/core';
import { Plus, Trash } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import { singularPluralFormat } from '../../../helpers/format';
import AppImage from '../../common/AppImage';
import AppCard from '../../common/AppCard';
import * as classes from '../../../styles/nestedStyles.module.css';

const PrintDesignEditorProductSidebarItem = ({
  ecomVendorProduct,
  formState,
  setFormState
}) => {
  const {} = useMediaQueryIndex();
  const [expanded, setExpanded] = useState(false);
  const selected = formState?.selectedProductId === ecomVendorProduct?.id;
  const designs = formState?.productDesigns.filter(
    (t) => t.productId === ecomVendorProduct.id
  );

  useEffect(() => {
    if (selected && !expanded) {
      setExpanded(true);
    }
  }, [selected]);

  return ecomVendorProduct ? (
    <Accordion
      onChange={() => {
        setExpanded(selected ? !expanded : true);
        setFormState({
          ...formState,
          selectedProductId: ecomVendorProduct.id,
          selectedProductDesignId: null
        });
      }}
      styles={{
        root: { padding: 0 },
        label: { padding: 0 },
        control: {
          padding: 0,
          paddingRight: 8
        },
        item: {
          border: 'none',
          outline: selected ? 'solid 1px dodgerblue' : 'solid 1px lightgrey',
          borderRadius: 8
        },
        content: { padding: 0 }
      }}
      value={selected && expanded ? ecomVendorProduct.id : ''}
      variant="contained"
    >
      <Accordion.Item
        style={{ backgroundColor: '#FFF' }}
        value={ecomVendorProduct.id}
      >
        <Accordion.Control>
          <AppFlexbox style={{ padding: 8, gap: 8 }}>
            <AppStack
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                overflow: 'hidden',
                border: 'solid 1px lightgrey'
              }}
            >
              <AppImage
                src={ecomVendorProduct.previewImage}
                style={{ width: '100%', height: '100%' }}
              />
            </AppStack>
            <AppStack style={{ gap: 0 }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                {ecomVendorProduct.name}
              </AppText>
              <AppText style={{ fontSize: 12, color: '#666' }}>
                {singularPluralFormat(designs.length, 'Design', 'Designs')}
              </AppText>
            </AppStack>
          </AppFlexbox>
        </Accordion.Control>
        <Accordion.Panel>
          <AppStack style={{ padding: 0, gap: 0 }}>
            <Divider />

            <AppStack style={{ padding: 4, gap: 4 }}>
              {designs
                .sort((a, b) => a.sort - b.sort)
                .map((p, index) => {
                  const designSelected =
                    formState.selectedProductDesignId === p.id;

                  return (
                    <AppCard
                      key={p.id}
                      className={classes['hover-action-card']}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          selectedProductDesignId: p.id
                        });
                      }}
                      radius={4}
                      shadow="none"
                      style={{
                        padding: 0,
                        border: designSelected
                          ? 'solid 1px dodgerblue'
                          : 'solid 1px lightgrey'
                      }}
                      withBorder
                    >
                      <AppFlexbox
                        style={{
                          padding: 4,
                          gap: 8,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <AppFlexbox
                          style={{
                            gap: 8,
                            alignItems: 'center',
                            width: '100%'
                          }}
                        >
                          <AppStack
                            style={{
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 25,
                              height: 25,
                              border: 'solid 1px lightgrey',
                              borderRadius: 4,
                              overflow: 'hidden'
                            }}
                          >
                            {p.previewImage ? (
                              <AppImage fit="contain" src={p.previewImage} />
                            ) : null}
                          </AppStack>

                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight: designSelected ? 500 : 'normal',
                              color: '#000',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {p.productName ?? `New design ${index + 1}`}
                          </AppText>
                        </AppFlexbox>

                        {!p.pkProduct && (
                          <ActionIcon
                            color="dark"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              setFormState((prev) => ({
                                ...prev,
                                confirmSelectedTemplateId: p.id
                              }));
                            }}
                            size={16}
                            variant="subtle"
                          >
                            <Trash />
                          </ActionIcon>
                        )}
                      </AppFlexbox>
                    </AppCard>
                  );
                })}

              <AppCard
                className={classes['hover-action-card']}
                onClick={() => {
                  setFormState({ ...formState, showTemplateCreateModal: true });
                }}
                radius={4}
                shadow="none"
                style={{ padding: 0, border: 'solid 1px dodgerblue' }}
                withBorder
              >
                <AppFlexbox
                  style={{ padding: 4, gap: 4, alignItems: 'center' }}
                >
                  <Plus color="dodgerblue" size={16} />
                  <AppText
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'dodgerblue'
                    }}
                  >
                    New Design
                  </AppText>
                </AppFlexbox>
              </AppCard>
            </AppStack>
          </AppStack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  ) : (
    <AppCard
      radius={8}
      shadow="none"
      style={{ padding: 0, minHeight: 56 }}
      withBorder
    >
      <AppStack style={{ width: '100%' }}>
        <AppFlexbox style={{ padding: 8, gap: 8, width: '100%' }}>
          <AppStack
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              overflow: 'hidden',
              border: 'solid 1px lightgrey'
            }}
          >
            <Skeleton height="100%" width="100%" />
          </AppStack>
          <AppStack style={{ gap: 5, flex: 1, width: '100%' }}>
            <Skeleton height={14} width="45%" />
            <Skeleton height={12} width="30%" />
          </AppStack>
        </AppFlexbox>
      </AppStack>
    </AppCard>
  );
};

PrintDesignEditorProductSidebarItem.propTypes = {
  ecomVendorProduct: PropTypes.object,
  formState: PropTypes.object,
  setFormState: PropTypes.func
};

export default PrintDesignEditorProductSidebarItem;
